//#region IMPORT
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';
import BUD_BASIC_02 from './BUD_BASIC_02';
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { showLoading, closeLoading } from '../../../../Reducer/LoadingReducer';
import GridFunctions from '../../../../Services/agGrid/ag-Grid-functions';
import { useSearch1, useSave1, useDelete1 } from './BUD_BASIC_02_QUERY';
import { BUD_BASIC_02_Schema } from './BUD_BASIC_02_Schema';
import { useSave } from '../../../../hooks/useSaveN';
import useDelete from '../../../../hooks/useDeleteN';
import useCompanyCustDep from '../../../../hooks/useCompanyCustDep';
import ExcelJS from 'exceljs';
//#endregion

//#region 상수
// 화면 MENU_ID
const SCREEN_MENU_ID = 'BudgetManagement';

const MonthFields = [
  'month01',
  'month02',
  'month03',
  'month04',
  'month05',
  'month06',
  'month07',
  'month08',
  'month09',
  'month10',
  'month11',
  'month12',
];

const GetCategoryGroupKey = (row) =>
  String(row?.categoryName ?? '').trim().toLowerCase();
//#endregion

const BUD_BASIC_02F = forwardRef((props, ref) => {
  //#region 변수
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const searchInfo = useSelector((state) => state.SearchInfo);

  const [viewMode, setViewMode] = useState('half'); // 'half' 또는 'quarter'

  //예산편성 및 집행 그리드
  const [rowData1, setRowData1] = useState([]);
  const [selectedRowData1, setSelectedRowData1] = useState([]);
  const [changedRowData1, setChangedRowData1] = useState([]);
  const [deletedRowData1, setDeletedRowData1] = useState([]);
  const selectedRowNosRef1 = useRef(new Set());
  const lastCheckedIndexRef1 = useRef(null);
  const currentRowIndexRef1 = useRef(null);

  const gridRef1 = useRef();
  const fileInputRef = useRef(null); // 엑셀 업로드용 파일 input
  //#endregion

  // 회사/거래처 선택 상태 관리 및 필수 여부 검증을 위한 Hook
  const { handleCoChange, handleCustChange, validateSelection } =
    useCompanyCustDep({ requireCompany: true, requireCust: true });

  const handleRowSelection1 = (_rowData, rowIndex) => {
    currentRowIndexRef1.current = rowIndex;
    // 선택 목록은 그리드 onSelectionChanged → getSelectedRows()가 단일 소스 (체크 시 이중 setState로 레이아웃 흔들림 방지)
  };

  //#region 쿼리
  const { refetch: Search1 } = useSearch1({
    onSuccess: (rows) => {
      // 원본 데이터 매핑
      const mappedData = rows.map((item, index) => ({
        ...item,
        no: index + 1,
        budCd: item.budId || '',
        remark: item.remark != null ? item.remark : '',
      }));

      // 조회 순서 유지 + 각 행의 합계/집행률 계산
      // 각 행의 합계/집행률 계산
      mappedData.forEach((item) => {
        // 월별 합계 계산
        const totalYearAmount = MonthFields.reduce((sum, field) => {
          const v = item[field];
          const num =
            typeof v === 'number' ? v : v != null && v !== '' ? Number(v) : 0;
          return sum + (isNaN(num) ? 0 : num);
        }, 0);

        item.totalYearAmount = totalYearAmount;
      });

      // categoryName별로 그룹화하여 합계 행 추가
      const finalData = [];
      const categoryGroups = new Map();

      // categoryName별로 그룹화
      mappedData.forEach((item) => {
        const categoryKey = GetCategoryGroupKey(item);
        if (!categoryGroups.has(categoryKey)) {
          categoryGroups.set(categoryKey, []);
        }
        categoryGroups.get(categoryKey).push(item);
      });

      // 각 그룹별로 데이터와 합계 행 추가
      let rowIndex = 1;
      categoryGroups.forEach((groupItems, categoryKey) => {
        // 그룹 내 각 행 추가
        groupItems.forEach((item) => {
          item.no = rowIndex++;
          finalData.push(item);
        });

        // 그룹별 합계 행 생성
        const groupPrevYearAmount = groupItems.reduce(
          (sum, row) => sum + (parseFloat(row.prevYearAmount) || 0),
          0
        );
        const groupPrevExecAmount = groupItems.reduce(
          (sum, row) => sum + (parseFloat(row.prevExecAmount) || 0),
          0
        );
        const groupPrevExecRate =
          groupPrevYearAmount > 0
            ? parseFloat(
              ((groupPrevExecAmount / groupPrevYearAmount) * 100).toFixed(2)
            )
            : 0;

        const groupMonthTotals = {};
        MonthFields.forEach((field) => {
          groupMonthTotals[field] = groupItems.reduce(
            (sum, row) => sum + (parseFloat(row[field]) || 0),
            0
          );
        });

        const groupTotalYearAmount = Object.values(groupMonthTotals).reduce(
          (sum, val) => sum + val,
          0
        );

        const summaryRow = {
          no: rowIndex++,
          categoryId: groupItems[0]?.categoryId ?? '',
          groupCategoryName: categoryKey,
          categoryName: '합계',
          itemName: '',
          prevYearAmount: groupPrevYearAmount,
          prevExecAmount: groupPrevExecAmount,
          prevExecRate: groupPrevExecRate,
          ...groupMonthTotals,
          totalYearAmount: groupTotalYearAmount,
          remark: '',
          isSummaryRow: true, // 합계 행 표시용 플래그
        };

        finalData.push(summaryRow);
      });
      setRowData1(finalData);
    },
  });
  const delete1 = useDelete1();
  const { runAsync: Save1Async } = useSave1();

  const deleteHandler1 = useDelete({
    gridRef: gridRef1,
    deleteMutation: delete1,
    updateDeleted: setDeletedRowData1,
    updateRowData: setRowData1,
    deletedRowData: deletedRowData1,
    rowData: rowData1,
    setChangedRowData: setChangedRowData1,
  });

  const BuildDefaultNewBudgetRow1 = useCallback(() => {
    const sample = rowData1.find(
      (r) => !r.isSummaryRow && r.no !== '합계'
    );
    return {
      budCd: sample?.budCd ?? '',
      categoryId: '',
      categoryName: '',
      itemId: '',
      itemName: '',
      prevYearAmount: 0,
      prevExecAmount: 0,
      prevExecRate: 0,
      month01: 0,
      month02: 0,
      month03: 0,
      month04: 0,
      month05: 0,
      month06: 0,
      month07: 0,
      month08: 0,
      month09: 0,
      month10: 0,
      month11: 0,
      month12: 0,
      totalYearAmount: 0,
      remark: '',
      isSummaryRow: false,
    };
  }, [rowData1]);

  // 조회
  const SearchLogic1 = async () => {
    ResetLogic1();
    if (!validateSelection()) return;
    await Search1();
  };
  // 저장
  const { run: Save1 } = useSave({
    menuId: SCREEN_MENU_ID,
    requiredLabel: '구분',
    schema: BUD_BASIC_02_Schema,
    getChangedRows: () => {
      const reorderedData = GridFunctions.reorderRowNum(
        changedRowData1,
        rowData1
      );
      return reorderedData.filter(
        (row) => (row.newYn === 'Y' || row.editYn === 'Y') && !row.deleteYn
      );
    },
    getDeletedRows: () => deletedRowData1,
    requestDelete: deleteHandler1.DeleteLogic,
    requestSave: (rows) => Save1Async(rows),
    refresh: () => SearchLogic1(),
    reset: () => ResetLogic1(),
  });

  // 초기화
  const ResetLogic1 = () => {
    setRowData1([]);
    setSelectedRowData1([]);
    setChangedRowData1([]);
    setDeletedRowData1([]);
    gridRef1?.current?.api?.deselectAll();
  };
  //#endregion

  // 분기/반기 합계 계산
  const calcQuarterHalfTotals = (row) => {
    const m = (key) => parseFloat(row[key]) || 0;

    return {
      q1Total: m('month01') + m('month02') + m('month03'),
      q2Total: m('month04') + m('month05') + m('month06'),
      q3Total: m('month07') + m('month08') + m('month09'),
      q4Total: m('month10') + m('month11') + m('month12'),

      h1Total:
        m('month01') +
        m('month02') +
        m('month03') +
        m('month04') +
        m('month05') +
        m('month06'),

      h2Total:
        m('month07') +
        m('month08') +
        m('month09') +
        m('month10') +
        m('month11') +
        m('month12'),
    };
  };

  //#region 인쇄
  // 예산편성 인쇄
  const PrintLogic1 = async () => {
    dispatch(showLoading());
    const filtered = rowData1.filter((r) => {
      if (r?.isSummaryRow) return false;
      const itemNm = String(r?.itemName ?? '').trim();
      const categoryNm = String(r?.categoryName ?? '').trim();
      if (itemNm === '합계') return false;
      if (categoryNm === '합계') return false;
      return true;
    });
    try {
      if (changedRowData1.length > 0) {
        dispatch(
          showSnackbar({
            message: '데이터를 저장 후 인쇄하세요.',
            type: 'warning',
          })
        );
        dispatch(closeLoading());
        return false;
      }

      if (!rowData1 || rowData1.length === 0) {
        dispatch(
          showSnackbar({
            message: '인쇄할 데이터가 없습니다.',
            type: 'warning',
          })
        );
        dispatch(closeLoading());
        return false;
      }

      const currentYear = searchInfo.year || dayjs().format('YYYY');
      const previousYear =
        searchInfo.previousYear || (parseInt(currentYear) - 1).toString();
      const nextYear = (parseInt(currentYear) + 1).toString();
      const todayDate = dayjs().format('YYYY-MM-DD');

      // 천단위 구분 기호 추가 함수
      const formatNumber = (value) => {
        if (value == null || value === '') return '0';
        const num = typeof value === 'number' ? value : Number(value);
        return isNaN(num) ? '0' : num.toLocaleString('ko-KR');
      };

      // 합계 데이터 계산 (숫자 값 먼저 계산)
      const prevYearAmount = filtered.reduce(
        (sum, row) => sum + (parseFloat(row.prevYearAmount) || 0),
        0
      );
      const prevExecAmount = filtered.reduce(
        (sum, row) => sum + (parseFloat(row.prevExecAmount) || 0),
        0
      );
      const prevExecRate =
        prevYearAmount !== 0 ? prevExecAmount / prevYearAmount : 0;
      const totalYearAmount = filtered.reduce(
        (sum, row) => sum + (parseFloat(row.totalYearAmount) || 0),
        0
      );

      const summaryData = {
        summaryPrevYearAmount: formatNumber(prevYearAmount),
        summaryPrevExecAmount: formatNumber(prevExecAmount),
        summaryPrevExecRate: formatNumber(prevExecRate),
        summaryMonth01: formatNumber(
          filtered.reduce((sum, row) => sum + (parseFloat(row.month01) || 0), 0)
        ),
        summaryMonth02: formatNumber(
          filtered.reduce((sum, row) => sum + (parseFloat(row.month02) || 0), 0)
        ),
        summaryMonth03: formatNumber(
          filtered.reduce((sum, row) => sum + (parseFloat(row.month03) || 0), 0)
        ),
        summaryMonth04: formatNumber(
          filtered.reduce((sum, row) => sum + (parseFloat(row.month04) || 0), 0)
        ),
        summaryMonth05: formatNumber(
          filtered.reduce((sum, row) => sum + (parseFloat(row.month05) || 0), 0)
        ),
        summaryMonth06: formatNumber(
          filtered.reduce((sum, row) => sum + (parseFloat(row.month06) || 0), 0)
        ),
        summaryMonth07: formatNumber(
          filtered.reduce((sum, row) => sum + (parseFloat(row.month07) || 0), 0)
        ),
        summaryMonth08: formatNumber(
          filtered.reduce((sum, row) => sum + (parseFloat(row.month08) || 0), 0)
        ),
        summaryMonth09: formatNumber(
          filtered.reduce((sum, row) => sum + (parseFloat(row.month09) || 0), 0)
        ),
        summaryMonth10: formatNumber(
          filtered.reduce((sum, row) => sum + (parseFloat(row.month10) || 0), 0)
        ),
        summaryMonth11: formatNumber(
          filtered.reduce((sum, row) => sum + (parseFloat(row.month11) || 0), 0)
        ),
        summaryMonth12: formatNumber(
          filtered.reduce((sum, row) => sum + (parseFloat(row.month12) || 0), 0)
        ),
        summaryTotalYearAmount: formatNumber(totalYearAmount),
        // 분기별/반기별 합계
        summaryQ1Total: formatNumber(
          filtered.reduce(
            (sum, r) =>
              sum +
              (parseFloat(r.month01) || 0) +
              (parseFloat(r.month02) || 0) +
              (parseFloat(r.month03) || 0),
            0
          )
        ),
        summaryQ2Total: formatNumber(
          filtered.reduce(
            (sum, r) =>
              sum +
              (parseFloat(r.month04) || 0) +
              (parseFloat(r.month05) || 0) +
              (parseFloat(r.month06) || 0),
            0
          )
        ),
        summaryQ3Total: formatNumber(
          filtered.reduce(
            (sum, r) =>
              sum +
              (parseFloat(r.month07) || 0) +
              (parseFloat(r.month08) || 0) +
              (parseFloat(r.month09) || 0),
            0
          )
        ),
        summaryQ4Total: formatNumber(
          filtered.reduce(
            (sum, r) =>
              sum +
              (parseFloat(r.month10) || 0) +
              (parseFloat(r.month11) || 0) +
              (parseFloat(r.month12) || 0),
            0
          )
        ),

        summaryH1Total: formatNumber(
          filtered.reduce(
            (sum, r) =>
              sum +
              (parseFloat(r.month01) || 0) +
              (parseFloat(r.month02) || 0) +
              (parseFloat(r.month03) || 0) +
              (parseFloat(r.month04) || 0) +
              (parseFloat(r.month05) || 0) +
              (parseFloat(r.month06) || 0),
            0
          )
        ),
        summaryH2Total: formatNumber(
          filtered.reduce(
            (sum, r) =>
              sum +
              (parseFloat(r.month07) || 0) +
              (parseFloat(r.month08) || 0) +
              (parseFloat(r.month09) || 0) +
              (parseFloat(r.month10) || 0) +
              (parseFloat(r.month11) || 0) +
              (parseFloat(r.month12) || 0),
            0
          )
        ),
      };

      const headerData = {
        currentYear: currentYear,
        previousYear: previousYear,
        nextYear: nextYear,
        todayDate: todayDate,
        custCd: searchInfo.custCd || '',
        ...summaryData,
      };

      // 일반 행 데이터 포맷팅
      const regularData = filtered.map((row) => {
        const totals = calcQuarterHalfTotals(row);

        return {
          ...row,
          // 구분, 구분상세 명시적 포함
          categoryName: row.categoryName || '',
          itemName: row.itemName || '',
          // 년도 포함
          currentYear: currentYear,
          previousYear: previousYear,
          nextYear: nextYear,
          // 작성일자 포함
          createDate: todayDate,
          // 숫자 형식 포맷팅 (천단위 구분기호 포함)
          prevYearAmount: Number(row.prevYearAmount),
          prevExecAmount: Number(row.prevExecAmount),
          prevExecRate: Number(row.prevExecRate),
          month01: Number(row.month01),
          month02: Number(row.month02),
          month03: Number(row.month03),
          month04: Number(row.month04),
          month05: Number(row.month05),
          month06: Number(row.month06),
          month07: Number(row.month07),
          month08: Number(row.month08),
          month09: Number(row.month09),
          month10: Number(row.month10),
          month11: Number(row.month11),
          month12: Number(row.month12),
          totalYearAmount: Number(row.totalYearAmount),
          // 비고 포함
          remark: row.remark || '',

          q1Total: Number(totals.q1Total),
          q2Total: Number(totals.q2Total),
          q3Total: Number(totals.q3Total),
          q4Total: Number(totals.q4Total),
          h1Total: Number(totals.h1Total),
          h2Total: Number(totals.h2Total),
        };
      });

      // 모든 데이터 합치기 (일반 행만)
      const tableData = regularData;

      const printData = {
        FileName: 'BUD_BASIC_02',
        HeaderData: JSON.stringify(headerData),
        TableData: JSON.stringify(tableData),
      };

      await Commons.Axios(
        'report',
        Global.base.useUrl + Global.urls.Reports.reports,
        printData
      ).then((response) => {
        const blobUrl = URL.createObjectURL(response.data);
        window.open(blobUrl, '_blank');
      });
    } catch (e) {
      console.log(e);
      dispatch(showSnackbar({ message: e.message, type: 'error' }));
    } finally {
      dispatch(closeLoading());
    }
  };

  // 예산안 인쇄
  const PrintLogic2 = async () => {
    dispatch(showLoading());
    try {
      if (changedRowData1.length > 0) {
        dispatch(
          showSnackbar({
            message: '데이터를 저장 후 인쇄하세요.',
            type: 'warning',
          })
        );
        dispatch(closeLoading());
        return false;
      }

      if (!rowData1 || rowData1.length === 0) {
        dispatch(
          showSnackbar({
            message: '인쇄할 데이터가 없습니다.',
            type: 'warning',
          })
        );
        dispatch(closeLoading());
        return false;
      }

      const currentYear = searchInfo.year || dayjs().format('YYYY');
      const previousYear =
        searchInfo.previousYear || (parseInt(currentYear) - 1).toString();
      const nextYear = (parseInt(currentYear) + 1).toString();
      const todayDate = dayjs().format('YYYY-MM-DD');

      // 천단위 구분 기호 추가 함수
      const formatNumber = (value) => {
        if (value == null || value === '') return '0';
        const num = typeof value === 'number' ? value : Number(value);
        return isNaN(num) ? '0' : num.toLocaleString('ko-KR');
      };

      // 합계 데이터 계산 (BUD_BASIC_03 레포트 구조에 맞게)
      // 2025년 결산: prevExecAmount (집행액)
      const prevYearSettlement = rowData1.reduce(
        (sum, row) => sum + (parseFloat(row.prevExecAmount) || 0),
        0
      );
      // 2026년 예산: totalYearAmount (연간 예산 총액)
      const nextYearBudget = rowData1.reduce(
        (sum, row) => sum + (parseFloat(row.totalYearAmount) || 0),
        0
      );

      const summaryData = {
        summaryPrevYearSettlement: formatNumber(prevYearSettlement), // 2025년 결산 합계
        summaryNextYearBudget: formatNumber(nextYearBudget), // 2026년 예산 합계
      };

      const headerData = {
        currentYear: currentYear,
        previousYear: previousYear,
        nextYear: nextYear,
        todayDate: todayDate,
        custCd: searchInfo.custCd || '',
        ...summaryData,
      };

      // 일반 행 데이터 포맷팅 (BUD_BASIC_03 레포트 구조에 맞게)
      const regularData = rowData1.map((row) => ({
        ...row,
        // 구분, 구분상세 명시적 포함
        categoryName: row.categoryName || '',
        itemName: row.itemName || '',
        // 년도 포함
        currentYear: currentYear,
        previousYear: previousYear,
        nextYear: nextYear,
        // 작성일자 포함
        createDate: todayDate,
        // 2025년 결산: prevExecAmount (집행액)
        prevYearSettlement: formatNumber(row.prevExecAmount),
        // 2026년 예산: totalYearAmount (연간 예산 총액)
        nextYearBudget: formatNumber(row.totalYearAmount),
        // 비고 포함
        remark: row.remark || '',
      }));

      // 모든 데이터 합치기 (일반 행만)
      const tableData = regularData;

      const printData = {
        FileName: 'BUD_BASIC_03', // 예산안 레포트 파일명
        HeaderData: JSON.stringify(headerData),
        TableData: JSON.stringify(tableData),
      };

      await Commons.Axios(
        'report',
        Global.base.useUrl + Global.urls.Reports.reports,
        printData
      ).then((response) => {
        const blobUrl = URL.createObjectURL(response.data);
        window.open(blobUrl, '_blank');
      });
    } catch (e) {
      console.log(e);
      dispatch(showSnackbar({ message: e.message, type: 'error' }));
    } finally {
      dispatch(closeLoading());
    }
  };
  //#endregion

  //#region AuthButton 이벤트
  useEffect(() => {
    if (props.filterClick2) {
      if (!validateSelection()) {
        props.setFilterClick2(false);
        return;
      }

      gridRef1.current.api.setQuickFilter(searchInfo.searchText || '');
      props.setFilterClick2(false);
    }
    if (props.searchClick2) {
      ResetLogic1();
      if (!validateSelection()) {
        props.setSearchClick2(false);
        return;
      }

      SearchLogic1();
      props.setSearchClick2(false);
    }
    if (props.addClick2) {
      if (!validateSelection()) {
        props.setAddClick2(false);
        return;
      }

      GridFunctions.addClick(gridRef1, BuildDefaultNewBudgetRow1());
      props.setAddClick2(false);
    }
    if (props.saveClick2) {
      if (!validateSelection()) {
        props.setSaveClick2(false);
        return;
      }

      gridRef1?.current?.api?.stopEditing();
      Save1();
      props.setSaveClick2(false);
    }
    if (props.printClick2) {
      if (!validateSelection()) {
        props.setPrintClick2(false);
        return;
      }

      PrintLogic1();
      props.setPrintClick2(false);
    }
    if (props.printClick2Plan) {
      if (!validateSelection()) {
        props.setPrintClick2Plan(false);
        return;
      }

      PrintLogic2();
      props.setPrintClick2Plan(false);
    }
    if (props.deleteClick2) {
      if (!validateSelection()) {
        props.setDeleteClick2(false);
        return;
      }

      const selected = selectedRowData1 || [];
      if (selected.length === 0) {
        dispatch(
          showSnackbar({
            message: '삭제할 행을 선택하세요.',
            type: 'warning',
          })
        );
        props.setDeleteClick2(false);
        return;
      }

      const allNew = selected.every((row) => row.newYn === 'Y');
      const anyNew = selected.some((row) => row.newYn === 'Y');

      if (allNew) {
        selected.forEach((row) => deleteHandler1.DeleteNewRow(row));
      } else if (anyNew) {
        dispatch(
          showSnackbar({
            message:
              '신규 행과 기존 행을 함께 삭제할 수 없습니다. 각각 선택해 주세요.',
            type: 'warning',
          })
        );
      } else {
        deleteHandler1.MarkedForDeletion(selected);
      }

      props.setDeleteClick2(false);
    }
  }, [
    props.filterClick2,
    props.searchClick2,
    props.addClick2,
    props.saveClick2,
    props.deleteClick2,
    props.printClick2,
    props.printClick2Plan,
    BuildDefaultNewBudgetRow1,
    validateSelection,
    searchInfo.searchText,
    selectedRowData1,
    deleteHandler1,
    dispatch,
  ]);
  //#endregion

  // ref를 통해 SearchLogic1 노출
  useImperativeHandle(ref, () => ({
    SearchLogic1,
  }));

  const handleToggleView = (event) => {
    setViewMode(event.target.checked ? 'quarter' : 'half');
  };

  // 엑셀 (안전보건관리 예산 편성) 업로드
  const ExcelUpload1 = async (file) => {
    try {
      dispatch(showLoading());

      const arrayBuffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);

      const worksheet = workbook.worksheets[0];
      if (!worksheet) {
        dispatch(
          showSnackbar({
            message: '엑셀 시트를 불러오지 못했습니다.',
            type: 'warning',
          })
        );
        dispatch(closeLoading());
        return;
      }

      // 8행부터 데이터 읽기
      const startRow = 8;
      const columnKeys = [
        'categoryName',
        'itemName',
        'month01',
        'month02',
        'month03',
        'month04',
        'month05',
        'month06',
        'month07',
        'month08',
        'month09',
        'month10',
        'month11',
        'month12',
        'remark',
      ];

      const uploadedData = [];
      let currentRow = startRow;
      let hasData = true;

      while (hasData) {
        const row = worksheet.getRow(currentRow);
        const rowData = {};

        // 첫 번째 컬럼(구분)이 비어있으면 데이터가 없는 것으로 간주
        const firstCellValue = row.getCell(1).value;
        if (!firstCellValue || firstCellValue.toString().trim() === '') {
          hasData = false;
          break;
        }

        columnKeys.forEach((key, index) => {
          const cell = row.getCell(index + 1);
          let value = cell.value;

          // 값이 없으면 빈 문자열 또는 0
          if (value === null || value === undefined) {
            value = key.includes('month') ? 0 : '';
          } else {
            // 숫자 필드는 숫자로 변환
            if (key.includes('month')) {
              value = typeof value === 'number' ? value : Number(value.toString().replace(/,/g, '')) || 0;
            } else {
              // 객체인 경우 문자열로 변환
              if (typeof value === 'object' && value !== null) {
                value = Array.isArray(value.richText) && value.richText.length > 0
                  ? value.richText.map(item => item?.text || '').join('')
                  : value.toString().trim();
              } else {
                value = value.toString().trim();
              }
            }
          }

          rowData[key] = value;
        });

        // 최소한 구분이나 구분상세가 있으면 추가
        const hasAnyValue = rowData.categoryName || rowData.itemName;

        if (hasAnyValue && rowData.categoryName !== '합계') {
          // 신규 행으로 표시
          rowData.newYn = 'Y';
          rowData.no = `new_${Date.now()}_${currentRow}`;
          // 합계 행 제외
          if (!rowData.categoryName.includes('합계')) {
            uploadedData.push(rowData);
          }
        }

        currentRow++;
        // 최대 1000행까지만 읽기 (무한 루프 방지)
        if (currentRow > startRow + 1000) {
          break;
        }
      }

      if (uploadedData.length === 0) {
        dispatch(
          showSnackbar({
            message: '읽을 수 있는 데이터가 없습니다.',
            type: 'warning',
          })
        );
        dispatch(closeLoading());
        return;
      }

      // 키값 생성 함수 (categoryName, itemName 조합)
      const makeKeyString = (item) => {
        const categoryName = item.categoryName !== null && item.categoryName !== undefined
          ? String(item.categoryName).trim()
          : '';
        const itemName = item.itemName !== null && item.itemName !== undefined
          ? String(item.itemName).trim()
          : '';
        return `${categoryName}|${itemName}`;
      };

      // 기존 데이터를 Map으로 변환 (키: categoryName|itemName, 값: 기존 행 데이터)
      const existingDataMap = new Map();
      rowData1.forEach((row) => {
        if (!row.isSummaryRow) {
          const key = makeKeyString(row);
          if (key && key !== '|') {
            existingDataMap.set(key, row);
          }
        }
      });

      // 엑셀 데이터의 키 Set 생성 (삭제 대상 찾기용)
      const excelKeySet = new Set();
      uploadedData.forEach((row) => {
        const key = makeKeyString(row);
        if (key && key !== '|') {
          excelKeySet.add(key);
        }
      });

      // 데이터 변경 여부 확인 함수
      const hasChanges = (existingRow, excelRow) => {
        const fieldsToCompare = [
          'month01',
          'month02',
          'month03',
          'month04',
          'month05',
          'month06',
          'month07',
          'month08',
          'month09',
          'month10',
          'month11',
          'month12',
          'remark',
        ];

        for (const field of fieldsToCompare) {
          const existingValue = existingRow[field] !== null && existingRow[field] !== undefined
            ? (typeof existingRow[field] === 'number' ? existingRow[field] : Number(existingRow[field]) || 0)
            : 0;
          const excelValue = excelRow[field] !== null && excelRow[field] !== undefined
            ? (typeof excelRow[field] === 'number' ? excelRow[field] : Number(excelRow[field]) || 0)
            : 0;

          if (field === 'remark') {
            const existingStr = String(existingRow[field] || '').trim();
            const excelStr = String(excelRow[field] || '').trim();
            if (existingStr !== excelStr) {
              return true;
            }
          } else {
            if (existingValue !== excelValue) {
              return true;
            }
          }
        }

        return false;
      };

      const newRows = [];
      const updatedRows = [];
      const updatedKeys = new Set();

      // 엑셀 데이터 처리
      for (const excelRow of uploadedData) {
        const key = makeKeyString(excelRow);

        if (!key || key === '|') {
          continue;
        }

        if (existingDataMap.has(key)) {
          const existingRow = existingDataMap.get(key);

          if (hasChanges(existingRow, excelRow)) {
            const updatedRow = {
              ...existingRow,
              ...excelRow,
              newYn: existingRow.newYn || 'N',
              editYn: 'Y',
              no: existingRow.no,
              budCd: existingRow.budCd || '',
              categoryId: existingRow.categoryId || '',
            };
            updatedRows.push(updatedRow);
            updatedKeys.add(key);
          }
        } else {
          newRows.push({
            ...excelRow,
            newYn: 'Y',
            no: `new_${Date.now()}_${Math.random()}`,
          });
        }
      }

      // 기존 데이터 중 엑셀에 없는 데이터는 삭제 대상으로 표시
      const rowsToDelete = [];
      rowData1.forEach((row) => {
        if (!row.isSummaryRow) {
          const key = makeKeyString(row);
          if (key && key !== '|' && !excelKeySet.has(key) && row.deleteYn !== 'Y') {
            rowsToDelete.push({
              ...row,
              deleteYn: 'Y',
            });
          }
        }
      });

      // 기존 데이터 업데이트
      const updatedRowData = rowData1.map((row) => {
        if (row.isSummaryRow) return row;
        const key = makeKeyString(row);
        if (key && updatedKeys.has(key)) {
          const updatedRow = updatedRows.find((ur) => makeKeyString(ur) === key);
          if (updatedRow) return updatedRow;
        }
        if (rowsToDelete.some((rd) => rd.no === row.no)) {
          return { ...row, deleteYn: 'Y' };
        }
        return row;
      });

      // 업데이트된 행과 신규 행을 합쳐서 그리드에 설정
      const finalRowData = [...updatedRowData, ...newRows];
      gridRef1.current.api.setRowData(finalRowData);

      // 변경된 데이터 설정
      const changedData = [...updatedRows, ...newRows];
      setChangedRowData1(changedData);

      // 삭제된 데이터 설정
      if (rowsToDelete.length > 0) {
        setDeletedRowData1((prev) => {
          const existingDeleted = prev || [];
          const newDeleted = rowsToDelete.filter(
            (rd) => !existingDeleted.some((ed) => ed.no === rd.no)
          );
          return [...existingDeleted, ...newDeleted];
        });
      }

      dispatch(
        showSnackbar({
          message: '엑셀 업로드가 완료되었습니다.',
          type: 'success',
        })
      );
    } catch (error) {
      console.error('Excel upload error:', error);
      dispatch(
        showSnackbar({
          message: `엑셀 업로드 중 오류가 발생했습니다: ${error.message}`,
          type: 'error',
        })
      );
    } finally {
      dispatch(closeLoading());
    }
  };

  // 엑셀 (안전보건관리 예산 편성) 다운로드
  const ExcelDownload1 = async () => {
    try {
      dispatch(showLoading());

      const workbook = new ExcelJS.Workbook();
      const templateUrl = getTemplatePath();

      const response = await fetch(templateUrl);
      const arrayBuffer = await response.arrayBuffer();
      await workbook.xlsx.load(arrayBuffer);

      // 템플릿에 그림이 없어도 ExcelJS load→write 후 유령 이미지/로고가 보이는 경우 완화 (BUD_BASIC_01 패턴)
      try {
        workbook.media = [];
        workbook.eachSheet((ws) => {
          if (ws && Array.isArray(ws._media)) {
            ws._media = [];
          }
        });
      } catch (e) {
        console.warn('[BUD_BASIC_02] 엑셀 미디어 초기화:', e);
      }

      const worksheet = workbook.worksheets[0];
      if (!worksheet) {
        dispatch(
          showSnackbar({
            message: '템플릿 시트를 불러오지 못했습니다.',
            type: 'warning',
          })
        );
        dispatch(closeLoading());
        return false;
      }

      // 템플릿 C5에 +1 연도 표시 (예: 조회연도 2025 -> 2026년도 예산)
      const currentYear = String(
        Number(searchInfo.year || dayjs().format('YYYY')) + 1
      );
      setPreserveStyle(worksheet.getCell('C5'), `${currentYear}년도 예산`);

      // 상단 타이틀에 연도 반영 + 작성일자 입력 (BUD_BASIC_01 패턴)
      const titleFull = `${currentYear}년 안전보건관리 예산 편성`;
      const createDateStr = dayjs().format('YYYY-MM-DD');

      const CellLooksLikeTitle = (cell) => {
        const s = cell?.value == null ? '' : String(cell.value);
        return (
          s.includes('안전보건관리') ||
          s.includes('예산') ||
          s.includes('편성')
        );
      };

      // 2행 B~L: 타이틀 병합 구역 우선 탐색
      let titleApplied = false;
      for (let c = 2; c <= 12 && !titleApplied; c += 1) {
        const cell = worksheet.getRow(2).getCell(c);
        if (CellLooksLikeTitle(cell)) {
          setPreserveStyle(cell, titleFull);
          titleApplied = true;
        }
      }
      // fallback: 상단(1~3행) 전체에서 타이틀 후보 탐색
      if (!titleApplied) {
        for (let r = 1; r <= 3 && !titleApplied; r += 1) {
          for (let c = 1; c <= 12 && !titleApplied; c += 1) {
            const cell = worksheet.getRow(r).getCell(c);
            if (CellLooksLikeTitle(cell)) {
              setPreserveStyle(cell, titleFull);
              titleApplied = true;
            }
          }
        }
      }
      // 마지막 fallback: 흔한 타이틀 셀(B2)
      if (!titleApplied) {
        setPreserveStyle(worksheet.getCell('B2'), titleFull);
      }

      // 작성일자: A4 라벨 옆 B4 (템플릿이 다르면 값만 들어가도 OK)
      try {
        setPreserveStyle(worksheet.getCell('B4'), createDateStr);
      } catch (e) {
        // ignore
      }

      // 그리드와 동일 순서: 상세 + 구분별 합계행(isSummaryRow), 삭제 표시 행 제외
      const exportRows = rowData1.filter((r) => !r.deleteYn);
      const detailOnly = exportRows.filter((r) => !r.isSummaryRow);

      // 데이터 삽입
      const startRow = 8;
      const columnKeys = [
        'categoryName',
        'itemName',
        'month01',
        'month02',
        'month03',
        'month04',
        'month05',
        'month06',
        'month07',
        'month08',
        'month09',
        'month10',
        'month11',
        'month12',
        'remark',
      ];

      /** 구분별 합계행 배경(그리드 #e8f5e9), 총합계행 #e3f2fd */
      const FILL_SUMMARY_CATEGORY = 'FFE8F5E9';
      const FILL_SUMMARY_GRAND = 'FFE3F2FD';

      const applyDataRowStyle = (
        row,
        key,
        cell,
        value,
        { bold = false, fillArgb = null } = {}
      ) => {
        setPreserveStyle(cell, value);
        cell.font = { name: 'Arial', size: 10, bold };
        let horizontalAlign = 'left';
        if (key.includes('month')) horizontalAlign = 'right';
        cell.alignment = {
          vertical: 'middle',
          horizontal: horizontalAlign,
          wrapText: false,
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF808080' } },
          left: { style: 'thin', color: { argb: 'FF808080' } },
          bottom: { style: 'thin', color: { argb: 'FF808080' } },
          right: { style: 'thin', color: { argb: 'FF808080' } },
        };
        if (fillArgb) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: fillArgb },
          };
        }
      };

      exportRows.forEach((item, index) => {
        const row = worksheet.getRow(startRow + index);
        const rowFill = item.isSummaryRow ? FILL_SUMMARY_CATEGORY : null;
        const rowBold = !!item.isSummaryRow;

        columnKeys.forEach((key, i) => {
          const cell = row.getCell(i + 1);
          let value = item[key];
          if (key.includes('month')) {
            value = value != null && value !== '' ? Number(value) : 0;
          } else {
            value = value || '';
          }
          applyDataRowStyle(row, key, cell, value, {
            bold: rowBold,
            fillArgb: rowFill,
          });
        });
        row.commit();
      });

      // 최종 합계 행: 상세 행만 월별 합산
      const monthKeys = columnKeys.filter((k) => k.includes('month'));
      const monthSums = {};
      monthKeys.forEach((k) => {
        monthSums[k] = detailOnly.reduce(
          (acc, r) => acc + (Number(r[k]) || 0),
          0
        );
      });

      const totalRowNum = startRow + exportRows.length;
      const totalRow = worksheet.getRow(totalRowNum);
      columnKeys.forEach((key, i) => {
        const cell = totalRow.getCell(i + 1);
        let value;
        if (key === 'categoryName') value = '합계';
        else if (key === 'itemName') value = '';
        else if (key.includes('month')) value = monthSums[key] ?? 0;
        else value = '';

        applyDataRowStyle(totalRow, key, cell, value, {
          bold: true,
          fillArgb: FILL_SUMMARY_GRAND,
        });
      });
      totalRow.commit();

      // 파일명 만들기
      const dateTimeStr = getCurrentFormattedDateTime('file');
      const methodName = `안전보건관리_예산_편성_${dateTimeStr}.xlsx`;

      // 다운로드
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = methodName;
      link.click();

      dispatch(closeLoading());
    } catch (error) {
      console.error('Excel download error:', error);
      dispatch(
        showSnackbar({
          message: `엑셀 다운로드 중 오류가 발생했습니다: ${error.message}`,
          type: 'error',
        })
      );
      dispatch(closeLoading());
    }
  };

  // 스타일 유지하며 값만 교체
  function setPreserveStyle(cell, value) {
    if (!cell) return;
    const { font, alignment, border, fill, numFmt } = cell;
    cell.value = value;
    cell.style = { font, alignment, border, fill, numFmt };
  }

  // 템플릿 경로 가져오기
  function getTemplatePath() {
    const base = `${Global.file.useUrl}files/ExcellExportFrom/BudgetManagement/`;
    return base + '안전보건관리_예산_편성.xlsx';
  }

  // 현재 날짜와 시간을 'YYYY-MM-DD HH:mm:ss' 형식으로 반환하는 함수
  function getCurrentFormattedDateTime(_type) {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    if (_type === 'data') {
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } else if (_type === 'file') {
      return `${year}${month}${day}_${hours}${minutes}${seconds}`;
    } else {
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }
  }

  return (
    <div className="wrap">
      <BUD_BASIC_02
        gridRef1={gridRef1}
        rowData1={rowData1}
        setRowData1={setRowData1}
        selectedRowData1={selectedRowData1}
        setSelectedRowData1={setSelectedRowData1}
        changedRowData1={changedRowData1}
        setChangedRowData1={setChangedRowData1}
        selectedRowNosRef1={selectedRowNosRef1}
        lastCheckedIndexRef1={lastCheckedIndexRef1}
        setViewMode={setViewMode}
        viewMode={viewMode}
        handleRowSelection1={handleRowSelection1}
        ResetLogic1={ResetLogic1}
        SearchLogic1={SearchLogic1}
        SaveLogic1={Save1}
        PrintLogic1={PrintLogic1}
        filterClick2={props.filterClick2}
        setFilterClick2={props.setFilterClick2}
        searchClick2={props.searchClick2}
        setSearchClick2={props.setSearchClick2}
        addClick2={props.addClick2}
        setAddClick2={props.setAddClick2}
        saveClick2={props.saveClick2}
        setSaveClick2={props.setSaveClick2}
        deleteClick2={props.deleteClick2}
        setDeleteClick2={props.setDeleteClick2}
        printClick2={props.printClick2}
        setPrintClick2={props.setPrintClick2}
        printClick2Plan={props.printClick2Plan}
        setPrintClick2Plan={props.setPrintClick2Plan}
        validateSelection={validateSelection}
        userInfo={userInfo}
        handleToggleView={handleToggleView}
        searchInfo={searchInfo}
        fileInputRef={fileInputRef}
        ExcelUpload1={ExcelUpload1}
        ExcelDownload1={ExcelDownload1}
        BuildDefaultNewBudgetRow1={BuildDefaultNewBudgetRow1}
        MarkedForDeletion1={deleteHandler1.MarkedForDeletion}
        RestoreRow1={deleteHandler1.RestoreRow}
        DeleteNewRow1={deleteHandler1.DeleteNewRow}
      />
    </div>
  );
});

export default BUD_BASIC_02F;
